import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import { Dialog, Box, Typography, Button, makeStyles } from "@material-ui/core";
import HistoryIcon from "@material-ui/icons/History";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import AuthContext from "connections/auth/AuthProvider.js";
import { successColor } from "assets/jss/material-dashboard-pro-react.js";
import { getScenarioDetails } from "../../viewServices/calls/apiCalls";
import { ScenarioSelector } from "../../sharedComponents/ScenarioSelector";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    headerTitle: {
      ...theme.typography.h6,
      fontSize: "1.125rem",
      color: theme.palette.secondary.main,
      fontWeight: "400",
      marginBottom: "0px",
      marginTop: "4px",
    },
    cancelButton: {
      minWidth: "80px",
      color: "#666",
      borderColor: "#ccc",
      fontSize: "0.8rem",
      padding: theme.spacing(0.5, 1.5),
      "&:hover": {
        backgroundColor: "#f5f5f5",
        borderColor: "#999",
      },
    },
    copyButton: {
      backgroundColor: clientColor,
      color: "#fff",
      minWidth: "100px",
      fontSize: "0.8rem",
      padding: theme.spacing(0.5, 2),
      "&:hover": {
        backgroundColor: successColor[1],
      },
      "&.Mui-disabled": {
        backgroundColor: "#e0e0e0",
        color: "#9e9e9e",
      },
    },
  }));

export default function DialogCopyScenario({
  open,
  onClose,
  scenarios,
  folders = [],
  availableVariants,
  onCopySuccess,
}) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails?.implementation_color || "#1976d2";
  const classes = useStyles(clientColor)();
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenarioSelection.copy_scenario",
  });

  const [selectedScenarioId, setSelectedScenarioId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCopy = async () => {
    setIsLoading(true);

    const toastId = toast.loading(t("loading_choices"));

    try {
      // 1. Busca detalhes do cenário selecionado
      const scenarioDetails = await getScenarioDetails(selectedScenarioId);

      if (
        !scenarioDetails ||
        !scenarioDetails.variants ||
        scenarioDetails.variants.length === 0
      ) {
        toast.update(toastId, {
          render: t("no_variants_found"),
          type: "warning",
          isLoading: false,
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
        });
        setIsLoading(false);
        return;
      }

      // 2. Mapear variants do cenário para seleção atual (matching por variant_name)
      const newSelection = mapScenarioVariantsToSelection(
        scenarioDetails.variants,
        availableVariants,
      );

      // 3. Chama callback para aplicar as escolhas
      if (onCopySuccess) {
        onCopySuccess(newSelection);
      }

      // 4. Success
      const copiedCount = Object.keys(newSelection).length;
      const totalCount = scenarioDetails.variants.length;

      toast.update(toastId, {
        render: t("copy_success", { copied: copiedCount, total: totalCount }),
        type: "success",
        isLoading: false,
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });

      // Reset e fecha
      setSelectedScenarioId("");
      onClose();
    } catch (error) {
      console.error("Failed to copy scenario choices:", error);
      toast.update(toastId, {
        render: t("copy_failed"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setSelectedScenarioId("");
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      {/* HEADER */}
      <Box
        borderBottom="1px solid #e0e0e0"
        style={{ padding: "8px 16px", position: "relative" }}
      >
        <h4 className={classes.headerTitle}>{t("title")}</h4>
      </Box>

      {/* CONTENT */}
      <Box p={3}>
        <Typography
          variant="body2"
          color="textSecondary"
          gutterBottom
          style={{ marginBottom: "12px", fontSize: "0.875rem" }}
        >
          {t("description")}
        </Typography>

        <ScenarioSelector
          scenarios={scenarios}
          folders={folders}
          value={selectedScenarioId || null}
          onChange={(id) => setSelectedScenarioId(id)}
          placeholder={t("select_placeholder")}
          disabled={isLoading}
          clientColor={clientColor}
          fullWidth
        />
      </Box>

      {/* FOOTER */}
      <Box
        p={2}
        display="flex"
        justifyContent="flex-end"
        gap={1}
        borderTop="1px solid #e0e0e0"
        style={{ gap: "8px" }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          className={classes.cancelButton}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleCopy}
          disabled={!selectedScenarioId || isLoading}
          className={classes.copyButton}
          startIcon={
            isLoading ? (
              <ThreeDots
                color="#ffffff"
                height={16}
                width={16}
                ariaLabel="copying"
              />
            ) : (
              <HistoryIcon />
            )
          }
        >
          {isLoading ? t("loading") : t("copy_button")}
        </Button>
      </Box>
    </Dialog>
  );
}

/**
 * Matching by VARIANT_NAME (not by ID)
 */
function mapScenarioVariantsToSelection(scenarioVariants, availableVariants) {
  const newSelection = {};

  scenarioVariants.forEach((scenarioVariant) => {
    const tableId = scenarioVariant.table_id;

    // Verificar se a table ainda existe
    if (!availableVariants[tableId]) {
      console.warn(`Table ${tableId} no longer exists, skipping...`);
      return; // Table não existe mais, ignora
    }

    // Buscar variant_id pelo variant_name (matching por nome)
    const matchingVariant = availableVariants[tableId].find(
      (v) => v.variant_name === scenarioVariant.variant_name,
    );

    if (matchingVariant) {
      newSelection[tableId] = matchingVariant.variant_id;
    } else {
      console.warn(
        `Variant "${scenarioVariant.variant_name}" not found for table ${tableId}`,
      );
      // Se não encontrar, não adiciona (deixa sem seleção = nenhum chip selected)
    }
  });

  return newSelection;
}

DialogCopyScenario.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scenarios: PropTypes.arrayOf(
    PropTypes.shape({
      scenario_id: PropTypes.number.isRequired,
      scenario_name: PropTypes.string.isRequired,
      status: PropTypes.string,
    }),
  ).isRequired,
  folders: PropTypes.array,
  availableVariants: PropTypes.object.isRequired,
  onCopySuccess: PropTypes.func,
};
